import React, { useMemo } from "react";
import { Box, Button, useMediaQuery } from "@mui/material";
import clsx from "clsx";
import FiltersArrowsSort from "./FiltersArrowsSort";
import { ClientSort, JourneyAnchors } from "../../constants";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { TagDto } from "@growth-machine-llc/stridist-api-client";
import { useCurrentWorkspace } from "../../hooks/useCurrentWorkspace";
import {
  NONE_TAG_ID,
  TagsFilters,
} from "../exercise-library/filters/TagsFilters";
import {
  ALL_OPTIONS,
  ALL_OPTIONS_TYPE,
} from "../../wrappers/router/coach/CoachClientsWrapper";

const useStyles = makeStyles((theme) => ({
  button: {
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    fontWeight: 700,
    fontSize: 14,
    display: "flex",
    justifyContent: "flex-start",

    "&.active": {
      color: theme.palette.primary.main,
    },
  },
  buttonsContainer: {
    paddingLeft: theme.spacing(3),
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    [theme.breakpoints.down("md")]: {
      justifyContent: "space-between",
      paddingLeft: 0,
    },
  },
  nameButton: {
    width: "28%",
    [theme.breakpoints.down("md")]: {
      width: "auto",
    },
  },
  tagButtonContainer: {
    width: "17%",
    [theme.breakpoints.down("md")]: {
      width: "auto",
    },
    "&.active": {
      color: theme.palette.primary.main,
    },
    display: "flex",
    justifyContent: "center",
  },
  tagButton: {
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    fontWeight: 700,
    display: "flex",
    justifyContent: "flex-start",

    "&.active": {
      color: theme.palette.primary.main,
    },
  },
  programButton: {
    width: "13%",
    [theme.breakpoints.down("md")]: {
      width: "auto",
    },
  },

  titleBlock: {
    display: "block",

    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },

  programProgress: {
    width: "25%",
  },

  title: {
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    fontWeight: 700,
    fontSize: 14,
    display: "flex",
    justifyContent: "flex-start",
    margin: theme.spacing(1.2, 1),
    textWrap: "nowrap",
    whiteSpace: "nowrap",
  },

  description: {
    height: 0,
    marginTop: theme.spacing(-1.5),
    marginLeft: theme.spacing(1),
    fontSize: 12,
    fontWeight: 500,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.border.primary,
  },
}));

type CoachClientsSortHeaderProps = {
  sortKey: string;
  handleChangeSortNameProgram: (value: boolean) => void;
  tagList: TagDto[];
  onChangeFilterTagId: (
    tagIds?: number[],
    allOptionsSelected?: boolean,
  ) => void;
  filterTagIds?: number[] | ALL_OPTIONS_TYPE;
  disabled: boolean;
};

const CoachClientsSortHeader = (props: CoachClientsSortHeaderProps) => {
  const {
    sortKey,
    handleChangeSortNameProgram,
    tagList,
    onChangeFilterTagId,
    filterTagIds,
    disabled,
  } = props;
  const s = useStyles();
  const { breakpoints } = useTheme();
  const xsDown = useMediaQuery(breakpoints.down("xs"));
  const [open, setOpen] = React.useState(false);

  const workspace = useCurrentWorkspace();

  const activeFilters = useMemo(
    () => ({
      tag: filterTagIds !== null && filterTagIds != ALL_OPTIONS,
      name: [ClientSort.NAME_ASC, ClientSort.NAME_DESC].includes(
        sortKey as ClientSort,
      ),
      program: [
        ClientSort.PROGRAM_END_ASC,
        ClientSort.PROGRAM_END_DESC,
      ].includes(sortKey as ClientSort),
    }),
    [sortKey, filterTagIds],
  );

  const isAllTagsSelected = filterTagIds === ALL_OPTIONS;

  const allTagIds = (tagList ?? []).map((tag) => tag.id);

  const tagIds = isAllTagsSelected ? [...allTagIds, NONE_TAG_ID] : filterTagIds;

  return (
    <Box className={s.buttonsContainer}>
      <Box className={s.nameButton}>
        <Button
          className={clsx(s.button, {
            active: activeFilters.name,
          })}
          children="Name"
          endIcon={
            <FiltersArrowsSort
              activeUp={sortKey === ClientSort.NAME_ASC}
              activeDown={sortKey === ClientSort.NAME_DESC}
            />
          }
          onClick={() => handleChangeSortNameProgram(true)}
        />
      </Box>
      {!disabled && (
        <>
          <Box
            className={clsx(s.tagButtonContainer, {
              active: activeFilters.tag,
            })}
          >
            {tagList?.length > 0 && tagIds?.length > 0 ? (
              <TagsFilters
                selectedTagIds={tagIds}
                onTagChange={onChangeFilterTagId}
                tags={tagList}
                className={clsx(s.tagButton, JourneyAnchors.CLIENTS_TAG, {
                  active: activeFilters.tag,
                })}
              />
            ) : (
              <Button
                className={clsx(s.tagButton)}
                sx={{
                  pointerEvents: "none",
                }}
                children="Tags"
              />
            )}
          </Box>

          <Box className={s.programButton}>
            <Button
              className={clsx(s.button, {
                active: activeFilters.program,
              })}
              children={"program"}
              endIcon={
                <FiltersArrowsSort
                  activeUp={sortKey === ClientSort.PROGRAM_END_ASC}
                  activeDown={sortKey === ClientSort.PROGRAM_END_DESC}
                />
              }
              onClick={() => handleChangeSortNameProgram(false)}
            />
            <Box className={s.description}>closest due date</Box>
          </Box>

          <Box className={clsx(s.programProgress, s.titleBlock)}>
            <Box className={s.title}>program progress</Box>
            <Box className={s.description}>closest due date</Box>
          </Box>

          {workspace.nutritionsEnabled && (
            <Box className={s.titleBlock}>
              <Box className={s.title}>nutrition</Box>
              <Box className={s.description}>last 7 days</Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CoachClientsSortHeader;
